import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCreditCard, FaUniversity, FaCheckCircle, FaArrowLeft, FaCopy } from 'react-icons/fa';

const Donate = () => {
  const [step, setStep] = useState(1); // 1: Main Page, 2: Details, 3: Payment/Bank
  const [amount, setAmount] = useState('50');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    paymentMethod: 'stripe',
    package: 'none'
  });
  const [errors, setErrors] = useState({});

  // Validation for Step 2
  const validateDetails = () => {
    let newErrors = {};
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Valid email is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const packages = [
    { id: 'bronze', name: 'Bronze Package', price: '500', color: '#CD7F32' },
    { id: 'silver', name: 'Silver Package', price: '1000', color: '#C0C0C0' },
    { id: 'gold', name: 'Gold Package', price: '2500', color: '#FFD700' },
    { id: 'sponsor', name: 'Main Sponsor', price: '5000', color: '#f9b50b' },
  ];

  return (
    <div className="min-h-screen bg-white font-sans overflow-x-hidden">
      {/* 1. HERO SECTION */}
      <section 
        className="relative h-[40vh] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('/Community Bash.jpeg')` }}
      >
        <div className="text-center text-white">
          <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-5xl md:text-6xl font-serif font-black uppercase">
            Donate
          </motion.h1>
          <div className="h-1.5 w-20 bg-[#f9b50b] mx-auto mt-4"></div>
          <p className="mt-4 uppercase tracking-[0.3em] text-xs font-bold">
            <Link to="/" className="hover:text-[#f9b50b] text-gray-300">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-white">Donate Now</span>
          </p>
        </div>
      </section>

      {/* 2. CONTENT AREA */}
      <div className="max-w-7xl mx-auto py-20 px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          
          {/* LEFT: WHY SUPPORT MATTERS (Static Content) */}
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-[#221f1f]">Why Your <span className="text-[#f9b50b]">Support</span> Matters</h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Every donation to the Illinois Interfaith Organization goes directly toward fostering peaceful coexistence and organizing community events.
            </p>
            <ul className="space-y-5">
              {[
                "Funding interfaith educational workshops.",
                "Supporting community services in Chicago and Hanover Park.",
                "Promoting the message of shared humanity."
              ].map((text, i) => (
                <li key={i} className="flex items-center gap-4 text-gray-700 font-medium">
                  <div className="bg-[#f9b50b]/20 p-1 rounded-full">
                    <FaCheckCircle className="text-[#f9b50b]" />
                  </div>
                  {text}
                </li>
              ))}
            </ul>
          </div>

          {/* RIGHT: INTERACTIVE FORM CARD */}
          <div className="bg-white rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-gray-100 overflow-hidden">
            <AnimatePresence mode="wait">
              
              {/* STEP 1: AMOUNT SELECTION */}
              {step === 1 && (
                <motion.div key="step1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-10">
                  <h3 className="text-2xl font-bold text-center mb-8 text-[#221f1f]">Choose Donation Amount</h3>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {['25', '50', '100'].map((val) => (
                      <button
                        key={val}
                        onClick={() => {setAmount(val); setFormData({...formData, package: 'none'})}}
                        className={`py-4 rounded-xl font-bold transition-all border-2 ${amount === val && formData.package === 'none' ? 'bg-[#f9b50b] border-[#f9b50b] text-white shadow-lg' : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100'}`}
                      >
                        ${val}
                      </button>
                    ))}
                  </div>

                  <div className="mb-8">
                    <label className="text-xs font-bold uppercase text-gray-400 ml-1">Custom Amount ($)</label>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => {setAmount(e.target.value); setFormData({...formData, package: 'none'})}}
                      className="w-full mt-2 p-4 bg-gray-50 border-2 border-transparent rounded-xl focus:border-[#f9b50b] focus:bg-white outline-none font-bold text-lg"
                    />
                  </div>

                  <div className="space-y-3 mb-8">
                    <p className="text-center text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Or Select a Package</p>
                    <div className="grid grid-cols-2 gap-3">
                      {packages.map((pkg) => (
                        <button
                          key={pkg.id}
                          onClick={() => {setAmount(pkg.price); setFormData({...formData, package: pkg.id})}}
                          className={`p-3 border-2 rounded-lg text-left transition-all ${formData.package === pkg.id ? 'border-[#f9b50b] bg-[#f9b50b]/5' : 'border-gray-100'}`}
                        >
                          <p className="text-[10px] font-bold uppercase text-gray-400">{pkg.name}</p>
                          <p className="font-black text-[#221f1f]">${pkg.price}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button 
                    onClick={() => setStep(2)}
                    className="w-full bg-[#221f1f] text-white py-5 rounded-xl font-bold text-lg hover:bg-[#f9b50b] transition-all uppercase tracking-widest shadow-xl"
                  >
                    Donate Now
                  </button>
                </motion.div>
              )}

              {/* STEP 2: PERSONAL DETAILS */}
              {step === 2 && (
                <motion.div key="step2" initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -50, opacity: 0 }} className="p-10">
                  <button onClick={() => setStep(1)} className="text-gray-400 hover:text-[#f9b50b] flex items-center gap-2 mb-6 text-sm font-bold uppercase">
                    <FaArrowLeft /> Back
                  </button>
                  <h3 className="text-2xl font-bold mb-2">Who's Giving Today?</h3>
                  <p className="text-gray-400 text-sm mb-8">Your information is secure and encrypted.</p>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <input name="firstName" placeholder="First Name *" onChange={handleInputChange} className={`w-full p-4 bg-gray-50 rounded-xl border-2 outline-none focus:border-[#f9b50b] ${errors.firstName ? 'border-red-400' : 'border-transparent'}`} />
                      <input name="lastName" placeholder="Last Name *" onChange={handleInputChange} className={`w-full p-4 bg-gray-50 rounded-xl border-2 outline-none focus:border-[#f9b50b] ${errors.lastName ? 'border-red-400' : 'border-transparent'}`} />
                    </div>
                    <input name="email" placeholder="Email Address *" onChange={handleInputChange} className={`w-full p-4 bg-gray-50 rounded-xl border-2 outline-none focus:border-[#f9b50b] ${errors.email ? 'border-red-400' : 'border-transparent'}`} />
                    <input name="organization" placeholder="Organization (Optional)" onChange={handleInputChange} className="w-full p-4 bg-gray-50 rounded-xl border-2 border-transparent outline-none focus:border-[#f9b50b]" />
                  </div>

                  <button 
                    onClick={() => validateDetails() && setStep(3)}
                    className="w-full mt-8 bg-[#221f1f] text-white py-5 rounded-xl font-bold uppercase tracking-widest hover:bg-[#f9b50b] transition-all"
                  >
                    Continue to Payment
                  </button>
                </motion.div>
              )}

              {/* STEP 3: PAYMENT & BANK DETAILS */}
              {step === 3 && (
                <motion.div key="step3" initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="p-10">
                  <button onClick={() => setStep(2)} className="text-gray-400 hover:text-[#f9b50b] flex items-center gap-2 mb-6 text-sm font-bold uppercase">
                    <FaArrowLeft /> Back
                  </button>
                  
                  <div className="bg-[#fcfcfc] p-4 rounded-xl border-2 border-dashed border-[#f9b50b] mb-8 text-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase">Total Donation</p>
                    <p className="text-3xl font-black text-[#221f1f]">${amount}</p>
                  </div>

                  <div className="space-y-4">
                    {/* Stripe Option */}
                    <button 
                      onClick={() => setFormData({...formData, paymentMethod: 'stripe'})}
                      className={`w-full flex items-center justify-between p-5 border-2 rounded-xl transition-all ${formData.paymentMethod === 'stripe' ? 'border-[#f9b50b] bg-white shadow-lg' : 'border-gray-50 bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-4 text-left">
                        <FaCreditCard className={formData.paymentMethod === 'stripe' ? 'text-[#f9b50b]' : 'text-gray-300'} size={24} />
                        <div>
                          <p className="font-bold text-sm">Credit Card / Stripe</p>
                          <p className="text-[10px] text-gray-400 uppercase">Instant Confirmation</p>
                        </div>
                      </div>
                      {formData.paymentMethod === 'stripe' && <FaCheckCircle className="text-[#f9b50b]" />}
                    </button>

                    {/* Bank Transfer Option */}
                    <button 
                      onClick={() => setFormData({...formData, paymentMethod: 'bank'})}
                      className={`w-full flex items-center justify-between p-5 border-2 rounded-xl transition-all ${formData.paymentMethod === 'bank' ? 'border-[#f9b50b] bg-white shadow-lg' : 'border-gray-50 bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-4 text-left">
                        <FaUniversity className={formData.paymentMethod === 'bank' ? 'text-[#f9b50b]' : 'text-gray-300'} size={24} />
                        <div>
                          <p className="font-bold text-sm">Offline Bank Transfer</p>
                          <p className="text-[10px] text-gray-400 uppercase">Wire Details Provided</p>
                        </div>
                      </div>
                      {formData.paymentMethod === 'bank' && <FaCheckCircle className="text-[#f9b50b]" />}
                    </button>
                  </div>

                  {/* Dynamic Bank Details Section */}
                  {formData.paymentMethod === 'bank' && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="mt-6 p-6 bg-gray-900 rounded-2xl text-white space-y-3">
                      <h4 className="text-[#f9b50b] text-xs font-bold uppercase tracking-widest border-b border-gray-700 pb-2">Bank Account Info</h4>
                      <div className="text-sm space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Bank:</span>
                          <span className="font-medium">Chase Bank Illinois</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Account Name:</span>
                          <span className="font-medium">IL Interfaith Org</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Account No:</span>
                          <span className="font-mono flex items-center gap-2">9920038817 <FaCopy className="text-[#f9b50b] cursor-pointer" /></span>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <button onClick={() => setStep(4)}className="w-full mt-8 bg-[#f9b50b] text-[#221f1f] py-5 rounded-xl font-black uppercase tracking-[0.2em] hover:bg-[#221f1f] hover:text-white transition-all shadow-xl">
                    Finalize Donation
                  </button>
                </motion.div>
              )}

            </AnimatePresence>
            {/* STEP 4: THANK YOU / SUCCESS */}
{step === 4 && (
  <motion.div 
    key="step4" 
    initial={{ scale: 0.8, opacity: 0 }} 
    animate={{ scale: 1, opacity: 1 }} 
    className="p-12 text-center"
  >
    <div className="w-20 h-20 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
      <FaCheckCircle size={40} />
    </div>
    
    <h2 className="text-3xl font-serif font-bold text-[#221f1f] uppercase mb-4">
      Thank You, {formData.firstName}!
    </h2>
    
    <p className="text-gray-500 leading-relaxed mb-8">
      Your generous donation of <span className="font-bold text-[#221f1f]">${amount}</span> has been received. 
      A confirmation receipt has been sent to <span className="text-[#f9b50b] font-medium">{formData.email}</span>. 
      Your support helps the Illinois Interfaith Organization continue its mission.
    </p>

    <div className="space-y-4">
      <Link 
        to="/" 
        className="block w-full bg-[#221f1f] text-white py-4 rounded-xl font-bold uppercase tracking-widest hover:bg-[#f9b50b] transition-all"
      >
        Return to Home
      </Link>
      
      <button 
        onClick={() => {
          setStep(1);
          setFormData({ firstName: '', lastName: '', email: '', organization: '', paymentMethod: 'stripe', package: 'none' });
        }}
        className="text-sm font-bold text-gray-400 hover:text-[#f9b50b] uppercase tracking-tight transition-colors"
      >
        Make another donation
      </button>
    </div>
  </motion.div>
)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Donate;